// Frontend entry point for the lab-test vocabulary. The presets, specimen types
// and `deriveOrderStatus` live in the cross-stack `@synapse/lab-tests` package
// (shared with the backend), so the tests this app offers and the tests the API
// accepts are the same declarations rather than two lists kept aligned by hand.
//
// What stays here is the part that genuinely belongs to the client: display
// labels, which need a translator.
//
// Mirrors how `src/lib/itemTypeConfig.ts` re-exports `@synapse/item-types`.

export * from '@synapse/lab-tests';

import type { LabOrderStatus, SpecimenType } from '@synapse/lab-tests';

/**
 * Translate a specimen type for display. The four types are a closed set with
 * matching `lab.specimen.*` translations, so unlike `translateUnit` there is no
 * pass-through case — an untranslated value here would be a missing locale key,
 * which is a bug rather than user-typed text.
 */
export function translateSpecimenType(
  t: (key: string) => string,
  specimenType: SpecimenType,
): string {
  return t(`lab.specimen.${specimenType}`);
}

/** Translate a derived order status for a badge or a filter label. */
export function translateOrderStatus(
  t: (key: string) => string,
  status: LabOrderStatus,
): string {
  return t(`lab.status.${status}`);
}

/**
 * Tone token for an order's status chip, mapped once here so the worklist, the
 * order detail header and the patient record cannot disagree about what
 * "resulted" looks like.
 *
 * `recollect_required` is the only one that reads as a problem — a rejected
 * sample is work that has to be redone — so it is the only one wearing the
 * danger tone. `awaiting_collection` is ordinary outstanding work.
 */
export type StatusTone = 'neutral' | 'info' | 'success' | 'danger' | 'muted';

export function orderStatusTone(status: LabOrderStatus): StatusTone {
  switch (status) {
    case 'resulted':
      return 'success';
    case 'partially_resulted':
      return 'info';
    case 'recollect_required':
      return 'danger';
    case 'cancelled':
      return 'muted';
    case 'awaiting_collection':
    case 'collected':
      return 'neutral';
  }
}
